import { SafeAreaView, ScrollView, SxProp, View } from 'dripsy';
import React, { useCallback, useState } from 'react';

import { PageContainer } from '@/features/earn/components/page/PageContainer';
import { PageItem } from '@/features/earn/components/page/PageItem';
import { DevToolsPageHeader } from '../components/DevToolsPageHeader';
import { DevToolsStackRoutes, DevToolsStackScreenProps } from '../types';
import { H6, TextBody, TextCaption } from '@/design-system/components/typography';
import { UniButton } from '@/design-system/components/buttons';
import { ButtonVariant } from '@/design-system/components/buttons/useButton.hook';
import { Dropdown } from '@/design-system/components/dropdown';
import { ButtonViewProps } from '@/design-system/components/buttons/UniButton';
import { SVGIconListArray } from '@/design-system/components/icons/Icon';
import { UniInput, UniSwitch } from '@/design-system/components/inputs';
import { SegmentSelect } from '@/design-system/components/buttons/segmentSelect/SegmentSelect';
import { HyperlinkButton, HyperlinkButtonProps } from '@/design-system/components/buttons/hyperlinkButton/HyperlinkButton';
import { Input } from '@/design-system/components/input/input/Input';

export const DevToolsButton: React.FC<DevToolsStackScreenProps<DevToolsStackRoutes.Button>> = (props) => {
  const [buttonProps, setButtonProps] = useState<Partial<ButtonViewProps>>({
    size: 'default',
    variant: ButtonVariant.primary,
    label: 'Button Label'
  });

  const segmentSelectOptions = [
    { id: 0, label: 'Default Option', value: 'option 1' },
    { id: 1, label: 'Semi Long Long Option', value: 'option 2' },
    { id: 2, label: 'Really Long Long Long Option', value: 'option 3' }
  ];
  const [segmentSelectValue, setSegmentSelectValue] = useState(0);

  const handleChangeButtonProps = useCallback((key: keyof ButtonViewProps, value: ButtonViewProps[keyof ButtonViewProps]) => {
    setButtonProps((prev) => ({ ...prev, [key]: value }));
  }, []);

  // Hyperlink Button
  const [hyperlinkButtonProps, setHyperlinkButtonProps] = useState<HyperlinkButtonProps>({
    label: 'Hyperlink Button',
    onPress: () => alert('Hyperlink Button Pressed'),
    icon: undefined,
    iconPosition: 'left',
    disabled: false
  });
  const handleChangeHyperlinkButtonProps = useCallback(
    (key: keyof HyperlinkButtonProps, value: HyperlinkButtonProps[keyof HyperlinkButtonProps]) => {
      setHyperlinkButtonProps((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

  return (
    <SafeAreaView sx={styles.container}>
      <ScrollView showsVerticalScrollIndicator={false}>
        <PageContainer sx={{ paddingX: '$10', paddingY: '$10', backgroundColor: 'transparent' }}>
          <DevToolsPageHeader currentPage={DevToolsStackRoutes.Button} />
          <View sx={{ gap: '$5' }}>
            {/* BUTTONS */}
            <PageItem sx={{ ...styles.pageItem, gap: '$5', zIndex: 1000 }}>
              <H6 semiBold label>
                Buttons
              </H6>
              {/* Button Size */}
              <View sx={{ zIndex: 1999 }}>
                <Dropdown
                  label="Button Size"
                  options={[
                    { label: 'Default', value: 'default' },
                    { label: 'Small', value: 'small' },
                    { label: 'Medium', value: 'medium' },
                    { label: 'Large', value: 'large' }
                  ]}
                  onValueChange={(value) => handleChangeButtonProps('size', value as ButtonViewProps['size'])}
                />
              </View>
              {/* Button Variant */}
              <View sx={{ zIndex: 1998 }}>
                <Dropdown
                  label="Button Variant"
                  options={Object.keys(ButtonVariant).map((key) => ({
                    label: key,
                    value: ButtonVariant[key as keyof typeof ButtonVariant]
                  }))}
                  onValueChange={(value) => handleChangeButtonProps('variant', value as ButtonVariant)}
                />
              </View>
              {/* Button Icon */}
              <View sx={{ flexDirection: 'row', gap: '$2', zIndex: 1997 }}>
                <Dropdown
                  label="Prefix Icon"
                  options={[
                    {
                      label: 'None',
                      value: ''
                    },
                    ...SVGIconListArray.map((key) => ({
                      label: key,
                      value: key
                    }))
                  ]}
                  onValueChange={(value) => handleChangeButtonProps('prefixIcon', value as ButtonViewProps['prefixIcon'])}
                />
                <Dropdown
                  label="Suffix Icon"
                  options={[
                    {
                      label: 'None',
                      value: ''
                    },
                    ...SVGIconListArray.map((key) => ({
                      label: key,
                      value: key
                    }))
                  ]}
                  onValueChange={(value) => handleChangeButtonProps('suffixIcon', value as ButtonViewProps['prefixIcon'])}
                />
              </View>
              <Input label="Button Label" value={buttonProps.label} onValueChange={(value) => handleChangeButtonProps('label', value)} />
              <UniButton
                size={buttonProps.size}
                variant={buttonProps.variant || ButtonVariant.primary}
                onPress={() => alert('On pressed button')}
                label={buttonProps.label}
                prefixIcon={buttonProps.prefixIcon}
                suffixIcon={buttonProps.suffixIcon}
                wrapperSx={{ flex: 1 }}
              />
              <PageItem sx={styles.pageItem}>
                <H6 semiBold label>
                  Usage
                </H6>
                <View>
                  <TextBody sx={{ fontFamily: 'monospace' }}>
                    {`
<UniButton
  size="${buttonProps.size}"
  variant={ButtonVariant.${buttonProps.variant}}
  onPress={() => alert('On pressed button')}
  label="${buttonProps.label}"
  ${buttonProps.prefixIcon ? `prefixIcon={${buttonProps.prefixIcon}}` : ''}
  ${buttonProps.suffixIcon ? `suffixIcon={${buttonProps.suffixIcon}}` : ''}
/>
                    `}
                  </TextBody>
                </View>
              </PageItem>
            </PageItem>

            {/* HYPERLINK BUTTONS */}
            <PageItem sx={{ ...styles.pageItem, gap: '$5', zIndex: 1000 }}>
              <H6 semiBold label>
                Hyperlink Button
              </H6>
              {/* Button Icon */}
              <View sx={{ flexDirection: 'row', gap: '$2', zIndex: 1997 }}>
                <Dropdown
                  label="Icon"
                  options={[
                    {
                      label: 'None',
                      value: ''
                    },
                    ...SVGIconListArray.map((key) => ({
                      label: key,
                      value: key
                    }))
                  ]}
                  onValueChange={(value) => handleChangeHyperlinkButtonProps('icon', value)}
                />
                <Dropdown
                  label="Icon Position"
                  options={[
                    {
                      label: 'Default',
                      value: ''
                    },
                    {
                      label: 'Left',
                      value: 'left'
                    },
                    {
                      label: 'Right',
                      value: 'right'
                    }
                  ]}
                  onValueChange={(value) => handleChangeHyperlinkButtonProps('iconPosition', value)}
                />
              </View>
              <View sx={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', zIndex: 1996 }}>
                <TextCaption sx={{}} semiBold>
                  Disabled
                </TextCaption>
                <UniSwitch
                  value={!!hyperlinkButtonProps.disabled}
                  onValueChange={(value) => handleChangeHyperlinkButtonProps('disabled', value)}
                />
              </View>
              <Input
                label="HyperlinkButton Label"
                value={hyperlinkButtonProps.label}
                onValueChange={(value) => handleChangeHyperlinkButtonProps('label', value)}
              />
              <View>
                <HyperlinkButton {...hyperlinkButtonProps} />
              </View>
              <PageItem sx={styles.pageItem}>
                <H6 semiBold label>
                  Usage
                </H6>
                <View>
                  <TextBody sx={{ fontFamily: 'monospace' }}>
                    {`
const [hyperlinkButtonProps, setHyperlinkButtonProps] = useState<HyperlinkButtonProps>({
    label: 'Hyperlink Button',
    onPress: () => alert('Hyperlink Button Pressed'),
    icon: undefined,
    iconPosition: 'left',
    disabled: false
  });
  const handleChangeHyperlinkButtonProps = useCallback(
    (key: keyof HyperlinkButtonProps, value: HyperlinkButtonProps[keyof HyperlinkButtonProps]) => {
      setHyperlinkButtonProps((prev) => ({ ...prev, [key]: value }));
    },
    []
  );

<HyperlinkButton
  label="${hyperlinkButtonProps.label}"
  onPress={() => alert('Hyperlink Button Pressed')}
  icon={${hyperlinkButtonProps.icon}}}
  iconPosition={${hyperlinkButtonProps.iconPosition || 'undefined'}}}
  disabled={${hyperlinkButtonProps.disabled}}
/>
                    `}
                  </TextBody>
                </View>
              </PageItem>
            </PageItem>
            <PageItem sx={styles.pageItem}>
              <H6 semiBold label>
                Segment Select
              </H6>
              <View sx={{ gap: '$5' }}>
                <View sx={{ width: 'auto' }}>
                  <SegmentSelect
                    data={segmentSelectOptions.map((option) => ({
                      label: option.label,
                      labelKey: option.label,
                      value: option.id
                    }))}
                    value={segmentSelectValue || 0}
                    onValueChange={(value) => setSegmentSelectValue(value)}
                  />
                </View>
                <PageItem sx={styles.pageItem}>
                  <H6 semiBold label>
                    Usage
                  </H6>
                  <View>
                    <TextBody sx={{ fontFamily: 'monospace' }}>
                      {`
<SegmentSelect
  data={segmentSelectOptions.map((option) => ({
    label: option.label,
    labelKey: option.label,
    value: option.id
  }))}
  value={segmentSelectValue || 0}
  onValueChange={(value) => setSegmentSelectValue(value)}
/>
                    `}
                    </TextBody>
                  </View>
                </PageItem>
              </View>
            </PageItem>
          </View>
        </PageContainer>
      </ScrollView>
    </SafeAreaView>
  );
};

enum StyleName {
  container = 'container',
  pageItem = 'pageItem'
}

const styles: Record<StyleName, SxProp> = {
  container: {
    height: '100%',
    width: '100%',
    backgroundColor: '$backgroundPrimary',
    zIndex: -100
  },
  pageItem: {
    width: '100%',
    padding: '$7',
    borderRadius: 10,
    gap: '$2',
    zIndex: -100
  }
};
